import types from 'dashboard/store/mutation-types';

class ReadState {
  revision = {};

  session = {};

  activeList = null;

  framesBlocked = false;

  // Access lost to single conversations ('list' or 'show'), until regained or
  // until the next account-wide revalidation reloads everything.
  revoked = new Map();

  // Bumped whenever direct (show) access to a conversation is revoked or
  // restored, so reads of that conversation started before do not apply.
  showChanges = new Map();

  // Newest access event (lost or gained) of each conversation.
  accessChanges = new Map();

  capture({ rootGetters }, conversationId) {
    const revision = this.revision;
    const session = this.session;
    const account = rootGetters.getCurrentAccountId;
    const user = rootGetters.getCurrentUserID;
    const key = Number(conversationId);
    const showChange = this.showChanges.get(key);
    const sameIdentity = () =>
      session === this.session &&
      account === rootGetters.getCurrentAccountId &&
      user === rootGetters.getCurrentUserID;
    return {
      sameIdentity,
      current: () =>
        sameIdentity() &&
        revision === this.revision &&
        (conversationId === undefined ||
          showChange === this.showChanges.get(key)),
    };
  }

  // Reset together with `revision`, which already expires every capture.
  resetConversations() {
    this.revoked = new Map();
    this.showChanges = new Map();
    this.accessChanges = new Map();
  }
}

const states = new WeakMap();
const stateFor = rootState => {
  if (!states.has(rootState)) states.set(rootState, new ReadState());
  return states.get(rootState);
};

export const invalidateConversationAccess = rootState => {
  const state = stateFor(rootState);
  state.revision = {};
  state.resetConversations();
};

export const invalidateConversationSession = rootState => {
  const state = stateFor(rootState);
  state.session = {};
  state.revision = {};
  state.activeList = null;
  state.framesBlocked = false;
  state.resetConversations();
};

const bumpShowChange = (state, key) => {
  state.showChanges.set(key, (state.showChanges.get(key) ?? 0) + 1);
};

// `scope` is 'list' (still readable, no longer listed) or 'show' (no access).
export const revokeConversationAccess = (rootState, conversationId, scope) => {
  const state = stateFor(rootState);
  const key = Number(conversationId);
  state.revoked.set(key, scope);
  if (scope === 'show') bumpShowChange(state, key);
};

export const restoreConversationAccess = (rootState, conversationId) => {
  const state = stateFor(rootState);
  const key = Number(conversationId);
  if (state.revoked.get(key) === 'show') bumpShowChange(state, key);
  state.revoked.delete(key);
};

export const conversationRevocation = (rootState, conversationId) =>
  conversationId === undefined || conversationId === null
    ? undefined
    : stateFor(rootState).revoked.get(Number(conversationId));

// Records of a conversation whose direct access was revoked are dropped.
export const readableConversationRecords = (rootState, records, idOf) =>
  records.filter(
    record => conversationRevocation(rootState, idOf(record)) !== 'show'
  );

// List-scoped records (list pages, notifications, contact history) drop every
// revoked conversation: one listed by an older read is no longer eligible.
export const listedConversationRecords = (
  rootState,
  records,
  idOf = record => record.id
) => records.filter(record => !conversationRevocation(rootState, idOf(record)));

// Orders the access events of one conversation: only the newest applies.
export const startConversationAccessChange = (context, conversationId) => {
  const state = stateFor(context.rootState);
  const key = Number(conversationId);
  const change = {};
  state.accessChanges.set(key, change);
  const { current } = state.capture(context);
  return () => current() && state.accessChanges.get(key) === change;
};

export const suspendConversationFrames = rootState => {
  stateFor(rootState).framesBlocked = true;
};

export const resumeConversationFrames = (rootState, isCurrent) => {
  if (isCurrent()) stateFor(rootState).framesBlocked = false;
};

export const conversationFramesReady = rootState =>
  !stateFor(rootState).framesBlocked;

// With a conversation id, the capture also expires when direct access to that
// conversation is revoked or restored.
export const captureConversationAccess = (context, conversationId) =>
  stateFor(context.rootState).capture(context, conversationId).current;

export const captureConversationSession = context =>
  stateFor(context.rootState).capture(context).sameIdentity;

export const readWithCurrentConversationAccess = async (context, load) => {
  const state = stateFor(context.rootState);
  const identity = state.capture(context);
  const attempt = async restarted => {
    if (!identity.sameIdentity()) return null;
    const checkpoint = state.capture(context);
    const response = await load(restarted);
    if (!identity.sameIdentity()) return null;
    return checkpoint.current() ? response : attempt(true);
  };
  return attempt(false);
};

export const readConversationPage = async (
  context,
  params,
  load,
  isCurrent
) => {
  let restarted = false;
  const response = await readWithCurrentConversationAccess(context, refresh => {
    restarted ||= refresh;
    if (refresh) params.page = 1;
    return load(params);
  });
  if (!response || !isCurrent()) return null;
  if (restarted) context.dispatch('conversationPage/reset');
  return response;
};

export const startConversationListRequest = context => {
  const state = stateFor(context.rootState);
  const request = {};
  state.activeList = request;
  context.commit(types.SET_LIST_LOADING_STATUS);
  const isCurrent = () => state.activeList === request;
  return {
    isCurrent,
    finish() {
      if (isCurrent()) {
        state.activeList = null;
        context.commit(types.CLEAR_LIST_LOADING_STATUS);
      }
    },
  };
};

export const invalidateConversationListRequests = context => {
  stateFor(context.rootState).activeList = null;
  context.commit(types.CLEAR_LIST_LOADING_STATUS);
};
